import { apiClient } from '@/api'
import type {
  ZaloOAStatusResponse,
  ZaloConnectUrlResponse,
  ZaloConnectRequest,
  ZaloConnectResponse,
  ZaloDisconnectResponse,
  ZaloChatsResponse,
  ZaloMessagesResponse,
  ZaloSendTextRequest,
  ZaloSendImageRequest,
  ZaloSendDocumentRequest,
  ZaloSendMessageResponse,
  ZaloMarkAsReadRequest,
  ZaloMarkAsReadResponse,
  ZaloBindChatResponse,
  ZaloMoveChatResponse,
} from '@/features/messenger/types/zalo.types'

const BASE_PATH = '/api/v1/messenger-providers/zalo'

// DEV-1675: OA у компании одна, её выбирает сервер — oa_id в запросы не передаём.
export const zaloApi = {
  async getOAStatus(): Promise<ZaloOAStatusResponse> {
    const response = await apiClient.get<ZaloOAStatusResponse>(`${BASE_PATH}/status`)
    return response.data
  },

  // DEV-1681: App ID знает только сервер, поэтому адрес подключения собирает он.
  async getConnectUrl(redirectUri: string, state: string): Promise<ZaloConnectUrlResponse> {
    const response = await apiClient.get<ZaloConnectUrlResponse>(`${BASE_PATH}/connect-url`, {
      params: { redirect_uri: redirectUri, state },
    })
    return response.data
  },

  async connect(body: ZaloConnectRequest): Promise<ZaloConnectResponse> {
    const response = await apiClient.post<ZaloConnectResponse>(`${BASE_PATH}/connect`, body)
    return response.data
  },

  async disconnect(): Promise<ZaloDisconnectResponse> {
    const response = await apiClient.post<ZaloDisconnectResponse>(`${BASE_PATH}/disconnect`, {})
    return response.data
  },

  // clientId — чаты клиента (видны тому, кому видна карточка); без него и с unbound —
  // только для communications:read_all.
  async getChats(params: {
    clientId?: string
    unbound?: boolean
    limit?: number
    offset?: number
    search?: string
  }): Promise<ZaloChatsResponse> {
    // DEV-302: курсорная пагинация (page_token — offset-строка)
    const query = {
      client_id: params.clientId,
      unbound: params.unbound || undefined,
      'pagination.pageSize': params.limit,
      'pagination.pageToken': params.offset ? String(params.offset) : undefined,
      search: params.search,
    }
    const response = await apiClient.get<ZaloChatsResponse>(`${BASE_PATH}/chats`, {
      params: query,
    })
    return response.data
  },

  async getMessages(
    userId: string,
    params: { limit?: number; offset?: number },
  ): Promise<ZaloMessagesResponse> {
    const encodedUserId = encodeURIComponent(userId)
    // DEV-302: курсорная пагинация (page_token — offset-строка)
    const query = {
      'pagination.pageSize': params.limit,
      'pagination.pageToken': params.offset ? String(params.offset) : undefined,
    }
    const response = await apiClient.get<ZaloMessagesResponse>(
      `${BASE_PATH}/chats/${encodedUserId}/messages`,
      { params: query },
    )
    return response.data
  },

  async sendText(body: ZaloSendTextRequest): Promise<ZaloSendMessageResponse> {
    const response = await apiClient.post<ZaloSendMessageResponse>(
      `${BASE_PATH}/messages/text`,
      body,
    )
    return response.data
  },

  async sendImage(body: ZaloSendImageRequest): Promise<ZaloSendMessageResponse> {
    const response = await apiClient.post<ZaloSendMessageResponse>(
      `${BASE_PATH}/messages/image`,
      body,
    )
    return response.data
  },

  async sendDocument(body: ZaloSendDocumentRequest): Promise<ZaloSendMessageResponse> {
    const response = await apiClient.post<ZaloSendMessageResponse>(
      `${BASE_PATH}/messages/document`,
      body,
    )
    return response.data
  },

  async markAsRead(body: ZaloMarkAsReadRequest): Promise<ZaloMarkAsReadResponse> {
    const response = await apiClient.post<ZaloMarkAsReadResponse>(
      `${BASE_PATH}/messages/read`,
      body,
    )
    return response.data
  },

  // DEV-1675: ручная привязка непривязанного чата к клиенту (communications:read_all).
  async bindChatToClient(userId: string, clientId: string): Promise<ZaloBindChatResponse> {
    const response = await apiClient.post<ZaloBindChatResponse>(
      `${BASE_PATH}/chats/${encodeURIComponent(userId)}/bind`,
      { client_id: clientId },
    )
    return response.data
  },

  // DEV-2472: перенос чата с лида-дубля на карточку клиента (communications:read_all):
  // чат и ключ Zalo уходят клиенту, дубль — в архив.
  async moveChatToClient(
    userId: string,
    fromClientId: string,
    toClientId: string,
  ): Promise<ZaloMoveChatResponse> {
    const response = await apiClient.post<ZaloMoveChatResponse>(
      `${BASE_PATH}/chats/${encodeURIComponent(userId)}/move`,
      { from_client_id: fromClientId, to_client_id: toClientId },
    )
    return response.data
  },
}
